'use client'

import type { NotificationToastProps } from '@maestro/commerce-blocks/components/notification/notification.types'

import classNames from 'classnames'
import { useTranslations } from 'next-intl'
import { useMemo } from 'react'

import { useNotificationToast } from '@maestro/commerce-blocks/components/notification/batons/use-notification-toast'
import { Button } from '@pearlconvert/ui/components/button'

const NotificationToast = ({
  id,
  title,
  description,
  announceMessage,
  type,
  onClose,
  showClose,
  content,
  isRemoved,
}: NotificationToastProps) => {
  const { toastRef, addToCartToast, handleClose } = useNotificationToast({
    id,
    content,
    onClose,
    title,
    description,
    announceMessage,
    type,
  })
  const t = useTranslations('commerce-blocks')
  const isCartUpdateToast = type === 'added-to-cart-notification'

  const toastCore = useMemo(() => {
    return (
      <>
        <div className="toast__message">
          <div className="toast__title">{title}</div>
          {description}
        </div>
        {addToCartToast && content}
        {showClose && onClose && (
          <Button
            type="empty"
            iconLeft="close"
            className="toast__close"
            onClick={handleClose}
            title={t('notification.close-message')}
          />
        )}
      </>
    )
  }, [
    addToCartToast,
    content,
    description,
    handleClose,
    onClose,
    showClose,
    t,
    title,
  ])

  return (
    <>
      <div className="toast-wrapper">
        <div
          className={classNames('toast', `toast--${type}`, {
            'toast--remove': isRemoved,
          })}
          role="dialog"
          aria-label={
            announceMessage || (typeof title === 'string' ? title : undefined)
          }
          aria-live={isCartUpdateToast ? 'off' : 'assertive'}
          aria-atomic="true"
          data-toast-id={id}
          ref={toastRef}
          tabIndex={0}
        >
          {toastCore}
        </div>
      </div>
    </>
  )
}

export default NotificationToast
